import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AppService } from '../app.service';
import { environment } from '../../../environments/environment';

/**
 * Stamps every outbound HTTP request with the current UI language so
 * language-aware backends (content-service, booking-service later) return
 * translated catalog data and error messages without each call site
 * having to remember the header.
 *
 * The header value is read fresh from {@link AppService#lang} on every
 * request, so flipping the language switcher in the topbar takes effect
 * on the very next call — no service restart, no token refresh.
 *
 * Special case: requests to the tripall flight/train/hotel gateway
 * always carry {@code Accept-Language: fa}, regardless of the UI
 * language. That upstream only returns Persian-localised city /
 * station / property names, and English requests yield blank `name`
 * fields on much of the catalogue. Forcing `fa` here keeps the agent
 * UI free to be English while the data still renders correctly.
 *
 * Requests that already carry an {@code Accept-Language} header are
 * left alone (caller knows best — e.g. a fetch for a specific
 * customer's preferred language).
 */
export const acceptLanguageInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.headers.has('Accept-Language')) {
    return next(req);
  }
  // Skip static asset fetches — those go to the dev server, not a
  // language-aware backend. In particular, the ngx-translate bundle
  // load (assets/i18n/{lang}.json) must NOT route through here: it
  // happens during AppService construction, so reading lang() during
  // that fetch creates an injection cycle that leaves the translate
  // store empty and every pipe rendering the raw key.
  if (req.url.startsWith('/assets/') || req.url.includes('/assets/i18n/')) {
    return next(req);
  }
  const tripallBase = environment.flightServiceBaseUrl;
  const lang = tripallBase && req.url.startsWith(tripallBase)
    ? 'fa'
    : inject(AppService).lang();
  return next(req.clone({ setHeaders: { 'Accept-Language': lang } }));
};
